import { Link, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import {
  invoicesApi,
  customersApi,
  paymentsApi,
  bankApi,
  type Customer,
  type InvoiceListItem,
  type InvoiceLineInput,
  fmtCents,
  VAT_OUTPUT_CLASSIFICATIONS,
} from "../lib/business";
import { tenantApi } from "../lib/tenant";
import { RupeeInput } from "../lib/RupeeInput";
import { ApiError, viewFileInNewTab } from "../lib/api";
import { RecordPaymentModal } from "../components/RecordPaymentModal";
import { AttachmentList } from "../components/AttachmentList";
import { HistoryDrawer } from "../components/HistoryDrawer";
import { StatusPill } from "../components/StatusPill";
import { TableShell } from "../components/TableShell";
import { StepCompact } from "../components/StepCompact";
import { useLangStore } from "../store/lang";
import { t } from "../i18n/strings";
import { localISOPlusDays, todayLocalISO } from "../lib/dates";

// SSCL sector multipliers — mirror backend lib/tax.ts SSCL_SECTOR_LIABLE_PCT.
const SSCL_SECTOR_LIABLE_PCT: Record<string, number> = {
  manufacturing: 85,
  services: 100,
  wholesale_retail: 50,
  distributor: 25,
  financial: 100,
};
// SSCL on a subtotal, computed exactly as backend lib/tax.ts ssclOnSubtotal:
// liable = subtotal × liable% / 100, then SSCL = liable × 2.5%. Two-step integer
// math so the live preview matches the posted total for fractional sector rates.
function ssclOnSubtotal(subtotalCents: bigint, sector?: string | null): bigint {
  const liable = (subtotalCents * BigInt(SSCL_SECTOR_LIABLE_PCT[sector ?? "services"] ?? 100)) / 100n;
  return (liable * 250n) / 10000n;
}

export function InvoicesPage() {
  return (
    <Routes>
      <Route index element={<InvoiceList />} />
      <Route path="new" element={<InvoiceNew />} />
      <Route path=":id/edit" element={<InvoiceEdit />} />
      <Route path=":id" element={<InvoiceDetail />} />
      <Route path="*" element={<Navigate to="" />} />
    </Routes>
  );
}

// ─── List ────────────────────────────────────────────────────────────────
function InvoiceList() {
  const [tab, setTab] = useState<string>("all");
  const [showTemplates, setShowTemplates] = useState(false);
  const status = tab === "all" || tab === "templates" ? undefined : tab;
  const isTemplatesTab = tab === "templates";
  const { data, isLoading } = useQuery({
    queryKey: ["invoices", tab],
    queryFn: () => isTemplatesTab ? invoicesApi.listTemplates() : invoicesApi.list(status),
  });
  const tabs = [
    { key: "all", label: "All" },
    { key: "draft", label: "Draft" },
    { key: "sent", label: "Sent" },
    { key: "partially_paid", label: "Partial" },
    { key: "paid", label: "Paid" },
    { key: "void", label: "Void" },
    { key: "templates", label: "Templates" },
  ];

  return (
    <>
      <div className="stage-header">
        <div>
          <div className="stage-eyebrow">Invoices</div>
          <h1 className="stage-title">
            Money <em>in.</em>
          </h1>
          <p className="stage-sub">
            Raise an invoice in four steps. Send via WhatsApp; payment posts to the ledger
            automatically.
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button
            type="button"
            className="btn btn-line"
            onClick={() => setShowTemplates(true)}
          >
            + From template
          </button>
          <Link to="new" className="btn btn-primary">
            + New invoice
          </Link>
        </div>
      </div>

      {showTemplates && <TemplatePickerModal onClose={() => setShowTemplates(false)} />}

      <div className="bill-tabs" style={billTabsStyle}>
        {tabs.map((t) => (
          <button
            key={t.key}
            className={`bill-tab ${tab === t.key ? "active" : ""}`}
            style={{
              padding: "7px 16px",
              borderRadius: 100,
              fontSize: 13,
              fontWeight: 500,
              color: tab === t.key ? "var(--ink)" : "var(--ink-3)",
              cursor: "pointer",
              background: tab === t.key ? "white" : "transparent",
              border: "none",
            }}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="card card-pad-lg" style={{ padding: 0 }}>
        {isLoading ? (
          <div style={{ padding: 24, color: "var(--ink-3)" }}>Loading…</div>
        ) : (
          <TableShell
            empty={(data?.invoices.length ?? 0) === 0}
            emptyNode={
              <>
                No invoices yet.{" "}
                <Link to="new">Create your first one →</Link>
              </>
            }
          >
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={th}>Number</th>
                  <th style={th}>Customer</th>
                  <th style={th}>Issued</th>
                  <th style={th}>Due</th>
                  <th style={{ ...th, textAlign: "right" }}>Total</th>
                  <th style={th}>Status</th>
                </tr>
              </thead>
              <tbody>
                {data?.invoices.map((inv) => (
                  <InvoiceRow key={inv.id} inv={inv} />
                ))}
              </tbody>
            </table>
          </TableShell>
        )}
      </div>
    </>
  );
}

function PortalLinkButton({ token }: { token: string }) {
  const [copied, setCopied] = useState(false);
  const url = `${window.location.origin}/clearbooks/pay/${token}`;
  async function copy() {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      window.prompt("Copy this URL and send it to your customer:", url);
    }
  }
  return (
    <button
      className="btn btn-line"
      onClick={copy}
      title={`Public pay link: ${url}`}
      style={{ fontSize: 12 }}
    >
      {copied ? "Copied!" : "Copy pay link"}
    </button>
  );
}

function TemplatePickerModal({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: ["invoice-templates"],
    queryFn: () => invoicesApi.listTemplates(),
  });
  const [cloning, setCloning] = useState<string | null>(null);

  async function pick(id: string) {
    setCloning(id);
    try {
      const cloned = await invoicesApi.clone(id);
      navigate(`/invoices/${cloned.id}/edit`);
      onClose();
    } catch {
      setCloning(null);
    }
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(14,22,18,0.4)",
        display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100,
      }}
    >
      <div
        className="card card-pad-lg modal-card"
        onClick={(e) => e.stopPropagation()}
        style={{ width: 560, maxWidth: "92vw", maxHeight: "82vh", overflow: "auto", display: "flex", flexDirection: "column", gap: 12 }}
      >
        <div>
          <div className="stage-eyebrow">New invoice from template</div>
          <h3 style={{ fontFamily: "'Fraunces', serif", fontWeight: 500, fontSize: 22, marginTop: 4 }}>
            Pick a template
          </h3>
          <p style={{ color: "var(--ink-3)", fontSize: 13, marginTop: 4 }}>
            Cloning creates a fresh draft you can review and send. Templates themselves never get sent.
          </p>
        </div>
        {isLoading ? (
          <div style={{ color: "var(--ink-3)" }}>Loading…</div>
        ) : (data?.invoices.length ?? 0) === 0 ? (
          <div
            style={{
              padding: 28, textAlign: "center",
              background: "var(--paper-2)", borderRadius: 8,
              color: "var(--ink-3)", fontSize: 13,
            }}
          >
            No templates yet. Open any draft invoice and click <strong>Mark as template</strong> to start.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {data?.invoices.map((t) => (
              <div
                key={t.id}
                style={{
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                  padding: 12, background: "var(--paper-2)", borderRadius: 8,
                }}
              >
                <div>
                  <div style={{ fontWeight: 500, fontSize: 14 }}>{t.customer.name}</div>
                  <div style={{ fontSize: 12, color: "var(--ink-3)" }}>
                    {t.invoice_number} · {(Number(t.total_cents) / 100).toLocaleString("en-LK", { minimumFractionDigits: 2 })} {t.currency}
                  </div>
                </div>
                <button
                  className="btn btn-primary"
                  onClick={() => pick(t.id)}
                  disabled={cloning === t.id}
                  style={{ fontSize: 12, padding: "6px 14px" }}
                >
                  {cloning === t.id ? "Cloning…" : "Use this"}
                </button>
              </div>
            ))}
          </div>
        )}
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <button className="btn btn-ghost" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

function InvoiceRow({ inv }: { inv: InvoiceListItem }) {
  return (
    <tr>
      <td style={td} data-label="Number">
        <Link to={inv.id} style={{ color: "var(--accent)", fontWeight: 500 }}>
          {inv.invoice_number}
        </Link>
      </td>
      <td style={td} data-label="Customer">{inv.customer.name}</td>
      <td style={td} data-label="Issued">{fmtDate(inv.issue_date)}</td>
      <td style={td} data-label="Due">{fmtDate(inv.due_date)}</td>
      <td style={{ ...td, textAlign: "right", fontFeatureSettings: "'tnum' 1", fontWeight: 500 }} data-label="Total">
        {fmtCents(inv.total_cents, inv.currency)}
      </td>
      <td style={td} data-label="Status">
        <StatusPill
          status={inv.status}
          dueDate={inv.due_date}
          paidCents={inv.paid_cents}
          totalCents={inv.total_cents}
        />
      </td>
    </tr>
  );
}

// ─── New (4-step) ────────────────────────────────────────────────────────
const STEPS = [
  { key: "customer", pill: "Step 1", name: "Customer", time: "10s" },
  { key: "details", pill: "Step 2", name: "Details", time: "20s" },
  { key: "items", pill: "Step 3", name: "Items & tax", time: "30s" },
  { key: "send", pill: "Step 4", name: "Send", time: "10s" },
];

function InvoiceNew() {
  const [step, setStep] = useState(0);
  const { lang } = useLangStore();
  const [customerId, setCustomerId] = useState<string | null>(null);
  const today = todayLocalISO();
  const dueDefault = localISOPlusDays(30);
  const [issueDate, setIssueDate] = useState(today);
  const [dueDate, setDueDate] = useState(dueDefault);
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");
  const [lines, setLines] = useState<InvoiceLineInput[]>([
    { description: "", lineType: "service", quantity: 1, unitPriceCents: 0, vatClassification: "taxable", vatRatePct: null, ssclApply: false, whtRatePct: null },
  ]);
  const [createdId, setCreatedId] = useState<string | null>(null);
  const [createdNumber, setCreatedNumber] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const navigate = useNavigate();
  const { data: tenant } = useQuery({ queryKey: ["tenant"], queryFn: () => tenantApi.getMe() });
  const { data: customersData } = useQuery({
    queryKey: ["customers", "all"],
    queryFn: () => customersApi.list(),
  });
  const customer = customersData?.customers.find((c) => c.id === customerId) ?? null;

  // Fix #9: once the tenant loads, sync the initial line's ssclApply/vatRatePct
  // to the tenant's flags. The "+ Add line" button already does this correctly;
  // this useEffect handles the very first line that was created before tenant
  // arrived from the network.
  useEffect(() => {
    if (!tenant) return;
    setLines((prev) => {
      // Only auto-correct lines that look pristine — the user hasn't already
      // overridden the toggle. We treat a still-zero unit price as pristine.
      const carries = (c: string | null | undefined) =>
        c == null || c === "taxable" || c === "deemed_specified" || c === "deemed_sdp";
      return prev.map((l) => {
        if (l.unitPriceCents !== 0) return l;
        return {
          ...l,
          vatRatePct: l.vatRatePct ?? (tenant.vat_registered ? 18 : null),
          ssclApply: carries(l.vatClassification) && !!tenant.sscl_liable && !!tenant.vat_registered,
        };
      });
    });
  }, [tenant]);

  function lineSubtotalCents(l: InvoiceLineInput): bigint {
    const qtyT = BigInt(Math.round(l.quantity * 1000));
    const price = BigInt(l.unitPriceCents);
    return (price * qtyT) / 1000n;
  }
  // SSCL applies before VAT; VAT then compounds on (base + SSCL). Mirror the
  // backend lib/tax.ts so the live preview matches what gets posted.
  const totals = lines.reduce(
    (acc, l) => {
      const sub = lineSubtotalCents(l);
      // SSCL at the sector-effective rate (matches backend lib/tax.ts B2).
      const sscl = l.ssclApply && tenant?.sscl_liable ? ssclOnSubtotal(sub, tenant?.sscl_sector) : 0n;
      const vatBase = sub + sscl;
      const vat = l.vatRatePct ? (vatBase * BigInt(Math.round(l.vatRatePct * 100))) / 10000n : 0n;
      const wht = l.whtRatePct ? (sub * BigInt(Math.round(l.whtRatePct * 100))) / 10000n : 0n;
      return {
        sub: acc.sub + sub,
        vat: acc.vat + vat,
        sscl: acc.sscl + sscl,
        wht: acc.wht + wht,
        total: acc.total + sub + vat + sscl - wht,
      };
    },
    { sub: 0n, vat: 0n, sscl: 0n, wht: 0n, total: 0n }
  );

  async function submitInvoice() {
    if (!customerId) return;
    setSubmitting(true);
    setErr(null);
    try {
      const created = await invoicesApi.create({
        customerId,
        reference: reference || null,
        issueDate,
        dueDate,
        currency: tenant?.base_currency ?? "LKR",
        notes: notes || null,
        lines: lines.map((l) => ({
          ...l,
          unitPriceCents: String(l.unitPriceCents),
        })),
      });
      setCreatedId(created.id);
      setCreatedNumber(created.invoice_number);
    } catch (e) {
      // Surface the server's explanation for known business-rule rejections
      // — every backend rejection comes with `error` (code) and often
      // `detail` (human message). Always show something specific.
      if (e instanceof ApiError) {
        const friendly: Record<string, string> = {
          export_customer_vat_not_allowed: t("invoice.exportGuardError", lang),
          duplicate_invoice_number: "An invoice with this number already exists. Numbering is automatic — refresh the form or contact support if this keeps happening.",
          customer_not_found: "Customer not found. Pick a customer from the list.",
          has_payments: "This invoice already has a payment — edits aren't allowed.",
          not_editable: "This invoice is no longer editable (already sent or void).",
        };
        setErr(friendly[e.code ?? ""] ?? e.detail ?? `Could not save the invoice (${e.code ?? e.status}).`);
      } else {
        setErr("Could not create invoice. Check the lines.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function sendInvoice(channels: string[]) {
    if (!createdId) return;
    setSubmitting(true);
    try {
      // Only hit /send when the user actually picked a channel. Calling
      // /send with no channels would flip the status to "sent" without any
      // delivery happening — wrong; the invoice should stay in `draft` until
      // the user explicitly sends it.
      if (channels.length > 0) {
        await invoicesApi.send(createdId, channels);
      }
      navigate(`/invoices/${createdId}`);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <div className="stage-header">
        <div>
          <div className="stage-eyebrow">Invoices</div>
          <h1 className="stage-title">
            New <em>invoice.</em>
          </h1>
          <p className="stage-sub">Four short steps. We'll handle numbering, totals, and the ledger.</p>
        </div>
        <Link to="/invoices" className="skip-link">
          ← Back to list
        </Link>
      </div>

      <div className="onboard-wrap">
        <StepCompact steps={STEPS} current={step} onJump={(i) => i <= step && setStep(i)} />
        <div className="onboard-progress">
          {STEPS.map((s, i) => (
            <button
              key={s.key}
              className={`onboard-step ${i === step ? "active" : ""} ${i < step ? "done" : ""}`}
              onClick={() => i <= step && setStep(i)}
              type="button"
              disabled={i > step}
              style={{ opacity: i > step ? 0.6 : 1 }}
            >
              <div className="step-pill">{s.pill}</div>
              <div className="step-name">{s.name}</div>
              <div className="step-time">{s.time}</div>
            </button>
          ))}
        </div>

        <div className="onboard-body">
          {step === 0 && (
            <CustomerStep
              customers={customersData?.customers ?? []}
              picked={customerId}
              onPick={(id) => {
                setCustomerId(id);
                setStep(1);
              }}
            />
          )}
          {step === 1 && (
            <DetailsStep
              issueDate={issueDate}
              setIssueDate={setIssueDate}
              dueDate={dueDate}
              setDueDate={setDueDate}
              reference={reference}
              setReference={setReference}
              notes={notes}
              setNotes={setNotes}
              onBack={() => setStep(0)}
              onNext={() => setStep(2)}
            />
          )}
          {step === 2 && (
            <ItemsStep
              lines={lines}
              setLines={setLines}
              currency={tenant?.base_currency ?? "LKR"}
              ssclLiable={!!tenant?.sscl_liable}
              vatRegistered={!!tenant?.vat_registered}
              totals={totals}
              onBack={() => setStep(1)}
              onNext={() => {
                setStep(3);
                submitInvoice();
              }}
              submitting={submitting}
            />
          )}
          {step === 3 && (
            <SendStep
              invoiceId={createdId}
              customer={customer}
              invoiceNumber={createdNumber}
              total={fmtCents(totals.total.toString(), tenant?.base_currency ?? "LKR")}
              onSend={sendInvoice}
              submitting={submitting}
              err={err}
            />
          )}
        </div>
      </div>
    </>
  );
}

function CustomerStep({
  customers,
  picked,
  onPick,
}: {
  customers: Customer[];
  picked: string | null;
  onPick: (id: string) => void;
}) {
  const [q, setQ] = useState("");
  const filtered = customers.filter(
    (c) => !q || c.name.toLowerCase().includes(q.toLowerCase()) || (c.email ?? "").toLowerCase().includes(q.toLowerCase())
  );
  return (
    <>
      <h2 className="onboard-headline">
        Who is this <em>for?</em>
      </h2>
      <p className="onboard-sub">Pick a customer. Recent customers appear first.</p>
      <input
        className="form-input filled"
        placeholder="Search customers…"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        style={{ marginBottom: 16 }}
      />
      {customers.length === 0 ? (
        <div
          className="card"
          style={{
            padding: 40,
            textAlign: "center",
            color: "var(--ink-3)",
            background: "var(--paper-2)",
            borderStyle: "dashed",
          }}
        >
          You don't have any customers yet.{" "}
          <Link to="/customers" style={{ color: "var(--accent)", fontWeight: 500 }}>
            Add your first one →
          </Link>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 10 }}>
          {filtered.slice(0, 12).map((c) => (
            <button
              key={c.id}
              className={`industry-card ${picked === c.id ? "selected" : ""}`}
              onClick={() => onPick(c.id)}
              type="button"
              style={{ padding: 16 }}
            >
              <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                <div className="avatar av-2">{c.name.slice(0, 2).toUpperCase()}</div>
                <div>
                  <div className="industry-name">{c.name}</div>
                  <div className="industry-sub">{c.email ?? c.phone ?? "—"}</div>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </>
  );
}

function DetailsStep(props: {
  issueDate: string;
  setIssueDate: (s: string) => void;
  dueDate: string;
  setDueDate: (s: string) => void;
  reference: string;
  setReference: (s: string) => void;
  notes: string;
  setNotes: (s: string) => void;
  onBack: () => void;
  onNext: () => void;
}) {
  return (
    <>
      <h2 className="onboard-headline">
        Invoice <em>details.</em>
      </h2>
      <p className="onboard-sub">Issue and due dates. Optional reference for the customer's PO number.</p>
      <div className="form-row">
        <div className="form-field">
          <label className="form-label">Issue date</label>
          <input className="form-input filled" type="date" value={props.issueDate} onChange={(e) => props.setIssueDate(e.target.value)} />
        </div>
        <div className="form-field">
          <label className="form-label">Due date</label>
          <input className="form-input filled" type="date" value={props.dueDate} onChange={(e) => props.setDueDate(e.target.value)} />
        </div>
      </div>
      <div className="form-row">
        <div className="form-field">
          <label className="form-label">Reference (their PO number, optional)</label>
          <input className="form-input" value={props.reference} onChange={(e) => props.setReference(e.target.value)} />
        </div>
        <div className="form-field">
          <label className="form-label">Notes (shown on invoice)</label>
          <input className="form-input" value={props.notes} onChange={(e) => props.setNotes(e.target.value)} />
        </div>
      </div>
      <div className="onboard-actions">
        <span />
        <div style={{ display: "flex", gap: 8 }}>
          <button className="btn btn-ghost" onClick={props.onBack}>← Back</button>
          <button className="btn btn-primary" onClick={props.onNext}>Continue →</button>
        </div>
      </div>
    </>
  );
}

// A classification "carries VAT/SSCL" — i.e. it's a standard taxable supply.
// Zero-rated / exempt / excluded / non-VAT-vendor supplies don't, so SSCL is
// not applicable either (Shamil 2026-06: SSCL is only charged where VAT is).
function classificationCarriesVat(c: string | null | undefined): boolean {
  return c === null || c === undefined || c === "taxable" || c === "deemed_specified" || c === "deemed_sdp";
}

function ItemsStep({
  lines,
  setLines,
  currency,
  ssclLiable,
  vatRegistered,
  totals,
  onBack,
  onNext,
  submitting,
  nextLabel,
  backLabel,
}: {
  lines: InvoiceLineInput[];
  setLines: (l: InvoiceLineInput[]) => void;
  currency: string;
  ssclLiable: boolean;
  vatRegistered: boolean;
  totals: { sub: bigint; vat: bigint; sscl: bigint; wht: bigint; total: bigint };
  onBack: () => void;
  onNext: () => void;
  submitting: boolean;
  nextLabel?: string;
  backLabel?: string;
}) {
  function update(i: number, patch: Partial<InvoiceLineInput>) {
    setLines(lines.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  }
  function add() {
    setLines([
      ...lines,
      {
        description: "",
        lineType: "service",
        quantity: 1,
        unitPriceCents: 0,
        vatClassification: "taxable",
        vatRatePct: vatRegistered ? 18 : null,
        // For a VAT-registered + SSCL-liable tenant, SSCL is mandatory on
        // taxable supplies — pre-tick it; the UI disables the toggle below.
        ssclApply: ssclLiable && vatRegistered,
        whtRatePct: null,
      },
    ]);
  }
  function remove(i: number) {
    setLines(lines.filter((_, idx) => idx !== i));
  }

  function lineErrors(l: InvoiceLineInput): string[] {
    const errors: string[] = [];
    if (!l.description.trim()) errors.push("Description is required.");
    if (!(l.quantity > 0)) errors.push("Quantity must be greater than zero.");
    if (!(Number(l.unitPriceCents) > 0)) errors.push("Unit price must be greater than zero.");
    const carries = classificationCarriesVat(l.vatClassification);
    if (carries && vatRegistered && (l.vatRatePct == null || l.vatRatePct < 0)) {
      errors.push("Taxable line needs a VAT rate (typical: 18%).");
    }
    return errors;
  }
  const anyLineHasErrors = lines.some((l) => lineErrors(l).length > 0);

  return (
    <>
      <h2 className="onboard-headline">
        Items & <em>tax.</em>
      </h2>
      <p className="onboard-sub">
        Per-line rates. Tax engine applies VAT, SSCL (2.5%), and WHT. Defaults follow the
        tenant's settings; override per line if needed.
      </p>

      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {lines.map((l, i) => (
          <div key={i} className="card" style={{ padding: 16, borderColor: lineErrors(l).length ? "var(--rose)" : undefined, borderWidth: lineErrors(l).length ? 1 : undefined, borderStyle: lineErrors(l).length ? "solid" : undefined }}>
            <div style={{ display: "grid", gridTemplateColumns: "2fr 70px 130px 130px", gap: 8, alignItems: "end" }}>
              <div className="form-field">
                <label className="form-label">Description</label>
                <input className="form-input" value={l.description} onChange={(e) => update(i, { description: e.target.value })} />
              </div>
              <div className="form-field">
                <label className="form-label">Qty</label>
                <input
                  className="form-input num"
                  type="number"
                  step="0.01"
                  value={l.quantity}
                  onChange={(e) => update(i, { quantity: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div className="form-field">
                <label className="form-label">Unit price ({currency})</label>
                <RupeeInput
                  cents={Number(l.unitPriceCents)}
                  onChange={(c) => update(i, { unitPriceCents: c })}
                />
              </div>
              <div className="form-field">
                <label className="form-label">Type</label>
                <select className="form-input" value={l.lineType} onChange={(e) => update(i, { lineType: e.target.value as "goods" | "service" })}>
                  <option value="service">Service</option>
                  <option value="goods">Goods</option>
                </select>
              </div>
            </div>
            <div style={{ marginTop: 10 }}>
              <div className="form-field">
                <label className="form-label">
                  VAT classification{" "}
                  <a
                    href="https://www.ird.gov.lk/en/Type%20of%20Taxes/SitePages/Value%20Added%20Tax%20(VAT).aspx"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ fontSize: 10, color: "var(--ink-4)", textDecoration: "underline", marginLeft: 6 }}
                  >
                    IRD VAT page
                  </a>
                </label>
                <select
                  className="form-input"
                  value={l.vatClassification ?? "taxable"}
                  onChange={(e) => {
                    const next = e.target.value as InvoiceLineInput["vatClassification"];
                    const prevCarries = classificationCarriesVat(l.vatClassification);
                    const nextCarries = classificationCarriesVat(next);
                    // When the classification CHANGES — e.g. exempt → taxable
                    // or taxable → zero-rated — force-repopulate the rate
                    // from the tenant default. Don't preserve `0` because a
                    // taxable line with 0% is almost always wrong. Within
                    // the same VAT-carrying classification, leave the rate
                    // alone so a user editing a non-18% rate isn't overridden.
                    const classificationChanged = prevCarries !== nextCarries;
                    update(i, {
                      vatClassification: next,
                      vatRatePct: nextCarries
                        ? (classificationChanged ? (vatRegistered ? 18 : null) : l.vatRatePct)
                        : 0,
                      // SSCL follows VAT — only charged where VAT is charged
                      // (Shamil 2026-06). Mandatory for VAT-reg + SSCL-liable
                      // tenants on taxable supplies; forced off on zero-rated /
                      // exempt / excluded.
                      ssclApply: nextCarries && ssclLiable && vatRegistered,
                    });
                  }}
                >
                  {VAT_OUTPUT_CLASSIFICATIONS.map((c) => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr auto", gap: 8, alignItems: "end", marginTop: 10 }}>
              <div className="form-field">
                <label className="form-label">VAT % (e.g. 18)</label>
                <input
                  className="form-input num"
                  type="number"
                  step="0.01"
                  value={l.vatRatePct ?? ""}
                  onChange={(e) => update(i, { vatRatePct: e.target.value ? parseFloat(e.target.value) : null })}
                />
              </div>
              <div className="form-field">
                <label className="form-label">WHT % (customer withholds)</label>
                <input
                  className="form-input num"
                  type="number"
                  step="0.01"
                  value={l.whtRatePct ?? ""}
                  onChange={(e) => update(i, { whtRatePct: e.target.value ? parseFloat(e.target.value) : null })}
                />
              </div>
              {(() => {
                const carries = classificationCarriesVat(l.vatClassification);
                // SSCL is mandatory whenever the line carries VAT AND the tenant
                // is VAT-registered AND SSCL-liable. Not applicable on
                // zero-rated / exempt / excluded supplies. Only manually
                // toggleable when SSCL liability is on but the line isn't a
                // standard taxable supply (rare edge case — keep editable).
                const mandatory = carries && ssclLiable && vatRegistered;
                const notApplicable = !carries;
                const disabled = mandatory || notApplicable || !ssclLiable;
                const hint = mandatory
                  ? "Mandatory for VAT-registered, SSCL-liable supplies"
                  : notApplicable
                    ? "Not applicable (zero-rated / exempt / excluded)"
                    : !ssclLiable
                      ? "Tenant not SSCL-liable"
                      : "";
                return (
                  <label
                    style={{ display: "flex", flexDirection: "column", gap: 2, fontSize: 13, color: "var(--ink-3)" }}
                    title={hint}
                  >
                    <span style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <input
                        type="checkbox"
                        checked={!!l.ssclApply}
                        onChange={(e) => update(i, { ssclApply: e.target.checked })}
                        disabled={disabled}
                      />
                      Apply SSCL 2.5%
                    </span>
                    {hint && <span style={{ fontSize: 10, color: "var(--ink-4)" }}>{hint}</span>}
                  </label>
                );
              })()}
              <button className="btn btn-ghost" type="button" onClick={() => remove(i)} disabled={lines.length === 1}>
                Remove
              </button>
            </div>
            {lineErrors(l).length > 0 && (
              <ul style={{
                marginTop: 10, padding: "8px 12px", listStyle: "none",
                background: "var(--rose-soft)", color: "var(--rose)",
                borderRadius: 6, fontSize: 12, display: "flex", flexDirection: "column", gap: 4,
              }}>
                {lineErrors(l).map((msg, j) => <li key={j}>· {msg}</li>)}
              </ul>
            )}
          </div>
        ))}
        <button className="btn btn-line" type="button" onClick={add} style={{ alignSelf: "flex-start" }}>
          + Add line
        </button>
      </div>

      <div
        className="card"
        style={{
          marginTop: 18,
          padding: 18,
          background: "var(--paper-2)",
        }}
      >
        <Row label="Subtotal" value={fmtCents(totals.sub.toString(), currency)} />
        <Row label="VAT" value={fmtCents(totals.vat.toString(), currency)} />
        <Row label="SSCL (2.5%)" value={fmtCents(totals.sscl.toString(), currency)} />
        <Row label="WHT (customer withholds)" value={`-${fmtCents(totals.wht.toString(), currency)}`} />
        <Row label="Total" value={fmtCents(totals.total.toString(), currency)} bold />
      </div>

      <div className="onboard-actions">
        <span />
        <div style={{ display: "flex", gap: 8 }}>
          <button className="btn btn-ghost" onClick={onBack} disabled={submitting}>{backLabel ?? "← Back"}</button>
          <button
            className="btn btn-primary"
            onClick={onNext}
            disabled={submitting || anyLineHasErrors}
            title={anyLineHasErrors ? "Fix the highlighted line(s) before saving." : ""}
          >
            {submitting ? "Saving…" : (nextLabel ?? "Save & continue →")}
          </button>
        </div>
      </div>
    </>
  );
}

function Row({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        padding: "6px 0",
        fontSize: 13,
        fontWeight: bold ? 500 : 400,
        color: bold ? "var(--ink)" : "var(--ink-3)",
        borderTop: bold ? "0.5px solid var(--ink)" : undefined,
        marginTop: bold ? 4 : 0,
        paddingTop: bold ? 8 : 6,
      }}
    >
      <span>{label}</span>
      <span className="num">{value}</span>
    </div>
  );
}

function SendStep({
  invoiceId,
  customer,
  invoiceNumber,
  total,
  onSend,
  submitting,
  err,
}: {
  invoiceId: string | null;
  customer: Customer | null;
  invoiceNumber: string | null;
  total: string;
  onSend: (channels: string[]) => void;
  submitting: boolean;
  err: string | null;
}) {
  const [whatsapp, setWhatsapp] = useState(true);
  const [email, setEmail] = useState(true);

  if (!invoiceNumber) {
    return <div style={{ color: "var(--ink-3)" }}>Saving invoice…</div>;
  }

  return (
    <div style={{ textAlign: "center" }}>
      <div className="welcome-mark" style={{ marginBottom: 16 }}>✓</div>
      <h2 className="onboard-headline">
        Saved as <em>{invoiceNumber}.</em>
      </h2>
      <p className="onboard-sub" style={{ marginLeft: "auto", marginRight: "auto" }}>
        How should we deliver it to {customer?.name ?? "the customer"}? Total {total}.
      </p>

      <div style={{ display: "flex", flexDirection: "column", gap: 10, maxWidth: 480, margin: "20px auto" }}>
        <div className={`toggle-card ${whatsapp ? "on" : ""}`} onClick={() => setWhatsapp((v) => !v)}>
          <div>
            <div className="toggle-card-name">WhatsApp</div>
            <div className="toggle-card-sub">
              {customer?.phone ? `Send to ${customer.phone}` : "No phone on file — disabled"}
            </div>
          </div>
          <div className={`toggle ${whatsapp ? "on" : ""}`} />
        </div>
        <div className={`toggle-card ${email ? "on" : ""}`} onClick={() => setEmail((v) => !v)}>
          <div>
            <div className="toggle-card-name">Email</div>
            <div className="toggle-card-sub">
              {customer?.email ? `Send to ${customer.email}` : "No email on file — disabled"}
            </div>
          </div>
          <div className={`toggle ${email ? "on" : ""}`} />
        </div>
      </div>

      {err && <div className="banner banner-error" style={{ maxWidth: 480, margin: "0 auto 12px" }}>{err}</div>}

      <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
        {invoiceId && (
          <button
            className="btn btn-line"
            onClick={() => viewFileInNewTab(`/invoices/${invoiceId}/pdf`)}
            title="Open the generated PDF in a new tab to check it before sending"
          >
            Preview PDF
          </button>
        )}
        <button className="btn btn-line" onClick={() => onSend([])}>
          Keep as draft
        </button>
        <button
          className="btn btn-primary"
          onClick={() => onSend([...(whatsapp ? ["whatsapp"] : []), ...(email ? ["email"] : [])])}
          disabled={submitting || (!whatsapp && !email)}
          title={!whatsapp && !email ? "Pick a delivery channel to enable" : ""}
        >
          {submitting ? "Sending…" : "Send & post to ledger →"}
        </button>
      </div>
      <p style={{ marginTop: 14, fontSize: 11, color: "var(--ink-4)", textAlign: "center" }}>
        Draft invoices can be edited later. Sending posts to the ledger and locks the lines.
      </p>
    </div>
  );
}

// ─── Edit (draft invoices only, before any payment) ─────────────────────
// Scope: ItemsStep + notes only. The header (customer / dates / reference)
// is read-only on edit because changing the customer would require re-
// running the export-customer guard against a different customer; instead
// the user voids and re-creates if the customer changes. 95% of edits are
// to the lines, this covers them.
function InvoiceEdit() {
  const navigate = useNavigate();
  // location.pathname under the SPA's basename is `/invoices/<id>/edit` —
  // but at runtime it can also be `/clearbooks/app/invoices/<id>/edit`
  // depending on whether React Router has stripped the basename yet. Pull
  // the id from the segment after "invoices" rather than relying on a
  // fixed positional index.
  const parts = location.pathname.split("/");
  const i = parts.indexOf("invoices");
  const id = i >= 0 ? parts[i + 1] : "";
  const { data: inv, isLoading } = useQuery({
    queryKey: ["invoice", id],
    queryFn: () => invoicesApi.get(id),
  });
  const { data: tenant } = useQuery({ queryKey: ["tenant"], queryFn: () => tenantApi.getMe() });
  const lang = useLangStore((s) => s.lang);

  const [notes, setNotes] = useState("");
  const [lines, setLines] = useState<InvoiceLineInput[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!inv) return;
    setNotes(inv.notes ?? "");
    setLines(
      inv.lines.map((l) => ({
        description: l.description,
        lineType: (l.line_type ?? "service") as InvoiceLineInput["lineType"],
        quantity: Number(l.quantity),
        unitPriceCents: Number(l.unit_price_cents),
        vatClassification: (l.vat_classification ?? "taxable") as InvoiceLineInput["vatClassification"],
        vatRatePct: l.vat_rate_pct != null ? Number(l.vat_rate_pct) : null,
        ssclApply: (Number(l.sscl_amount_cents ?? "0") > 0),
        whtRatePct: l.wht_rate_pct != null ? Number(l.wht_rate_pct) : null,
      })),
    );
  }, [inv?.id]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Live totals — same shape as the create flow's `totals`.
  const totals = lines.reduce(
    (acc, l) => {
      const qtyT = BigInt(Math.round((l.quantity || 0) * 1000));
      const price = BigInt(l.unitPriceCents || 0);
      const sub = (price * qtyT) / 1000n;
      const vatBase = sub;
      const vat = l.vatRatePct ? (vatBase * BigInt(Math.round(l.vatRatePct * 100))) / 10000n : 0n;
      const sscl = l.ssclApply ? (sub * 25n) / 1000n : 0n;
      const wht = l.whtRatePct ? (sub * BigInt(Math.round(l.whtRatePct * 100))) / 10000n : 0n;
      return {
        sub: acc.sub + sub,
        vat: acc.vat + vat,
        sscl: acc.sscl + sscl,
        wht: acc.wht + wht,
        total: acc.total + sub + vat + sscl - wht,
      };
    },
    { sub: 0n, vat: 0n, sscl: 0n, wht: 0n, total: 0n },
  );

  if (isLoading || !inv) return <div className="card">Loading…</div>;
  if (inv.status !== "draft" || BigInt(inv.paid_cents ?? "0") !== 0n) {
    return (
      <div className="card card-pad-lg">
        <h3 style={{ fontFamily: "'Fraunces', serif" }}>Can't edit</h3>
        <p style={{ color: "var(--ink-3)" }}>
          This invoice is {inv.status === "draft" ? "partially paid" : `${inv.status}`} and can't be edited.
          For a posted invoice the safe fix is to void it and raise a new one.
        </p>
        <Link to={`/invoices/${inv.id}`} className="btn btn-line">Back to invoice</Link>
      </div>
    );
  }

  async function save() {
    setSubmitting(true);
    setErr(null);
    try {
      await invoicesApi.update(id, {
        customerId: inv!.customer.id,
        reference: inv!.reference ?? null,
        issueDate: inv!.issue_date.slice(0, 10),
        dueDate: inv!.due_date.slice(0, 10),
        currency: inv!.currency,
        notes: notes || null,
        lines: lines.map((l) => ({ ...l, unitPriceCents: String(l.unitPriceCents) })),
      });
      navigate(`/invoices/${id}`);
    } catch (e) {
      if (e instanceof ApiError) {
        const friendly: Record<string, string> = {
          has_payments: "This invoice already has a payment — edits aren't allowed.",
          not_editable: "This invoice is no longer editable.",
          export_customer_vat_not_allowed: t("invoice.exportGuardError", lang),
        };
        setErr(friendly[e.code ?? ""] ?? e.detail ?? `Could not save (${e.code ?? e.status}).`);
      } else {
        setErr("Could not save invoice.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  const ssclLiable = !!tenant?.sscl_liable;
  const vatRegistered = !!tenant?.vat_registered;

  return (
    <>
      <div className="stage-header" style={{ display: "flex", justifyContent: "space-between" }}>
        <div>
          <h1 className="stage-title">Edit {inv.invoice_number}</h1>
          <p className="stage-sub">
            Draft invoice for {inv.customer.name}. Customer + dates are locked once created — to change those, void + raise a new one.
          </p>
        </div>
        <Link to={`/invoices/${inv.id}`} className="btn btn-ghost">Cancel</Link>
      </div>

      <ItemsStep
        lines={lines}
        setLines={setLines}
        currency={inv.currency}
        vatRegistered={vatRegistered}
        ssclLiable={ssclLiable}
        totals={totals}
        onBack={() => navigate(`/invoices/${inv.id}`)}
        onNext={save}
        submitting={submitting}
        nextLabel="Save changes"
        backLabel="Cancel"
      />

      <div className="card card-pad-lg" style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 10 }}>
        <div className="form-field">
          <label className="form-label">Notes</label>
          <textarea className="form-input" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </div>
        {err && <div className="banner banner-error">{err}</div>}
      </div>
    </>
  );
}

// ─── Detail ──────────────────────────────────────────────────────────────
function InvoiceDetail() {
  const id = location.pathname.split("/").pop()!;
  const { data, isLoading } = useQuery({
    queryKey: ["invoice", id],
    queryFn: () => invoicesApi.get(id),
  });
  const qc = useQueryClient();
  // All hooks must run on every render — the early return below would
  // change hook count between renders otherwise (React error #310).
  const navigate = useNavigate();
  const [paying, setPaying] = useState(false);

  if (isLoading || !data) return <div style={{ color: "var(--ink-3)" }}>Loading…</div>;

  const inv = data;

  async function send() {
    await invoicesApi.send(inv.id, ["whatsapp", "email"]);
    qc.invalidateQueries({ queryKey: ["invoice", inv.id] });
  }
  async function voidInvoice() {
    if (!confirm("Void this invoice? This creates a reversing ledger entry.")) return;
    try {
      await invoicesApi.void(inv.id);
      qc.invalidateQueries({ queryKey: ["invoice", inv.id] });
    } catch (e) {
      // Backend now refuses with 409 `has_payments` — guide the user to
      // reverse the payment first.
      const err = e as { code?: string; detail?: string; message?: string };
      if (err.code === "has_payments") {
        alert(
          `${err.detail || "This invoice has payments allocated."}\n\nScroll to the Payments section below and click Reverse next to the payment, then try Void again.`,
        );
      } else {
        alert(err.detail || err.message || "Could not void this invoice.");
      }
    }
  }
  async function clone() {
    try {
      const cloned = await invoicesApi.clone(inv.id);
      navigate(`/invoices/${cloned.id}/edit`);
    } catch (e) {
      alert(e instanceof Error ? e.message : "Could not clone the invoice.");
    }
  }
  async function toggleTemplate() {
    await invoicesApi.markTemplate(inv.id, !inv.is_template);
    qc.invalidateQueries({ queryKey: ["invoice", inv.id] });
    qc.invalidateQueries({ queryKey: ["invoices"] });
  }

  return (
    <>
      <div className="stage-header">
        <div>
          <div className="stage-eyebrow">Invoice {inv.invoice_number}</div>
          <h1 className="stage-title">
            {inv.customer.name}<em>.</em>
          </h1>
          <p className="stage-sub">
            Issued {fmtDate(inv.issue_date)} · Due {fmtDate(inv.due_date)}
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <StatusPill
            status={inv.status}
            dueDate={inv.due_date}
            paidCents={inv.paid_cents}
            totalCents={inv.total_cents}
            size="md"
          />
          {inv.status === "draft" && BigInt(inv.paid_cents ?? "0") === 0n && (
            <>
              <Link className="btn btn-line" to={`/invoices/${inv.id}/edit`}>
                Edit
              </Link>
              {!inv.is_template && (
                <button className="btn btn-primary" onClick={send}>Send →</button>
              )}
            </>
          )}
          {!inv.is_template && (
            <button className="btn btn-line" onClick={clone}>Clone</button>
          )}
          {inv.status === "draft" && BigInt(inv.paid_cents ?? "0") === 0n && (
            <button
              className="btn btn-ghost"
              onClick={toggleTemplate}
              title={inv.is_template
                ? "Make this a regular draft invoice you can send."
                : "Convert this draft into a reusable template. Templates never get sent."}
            >
              {inv.is_template ? "Unmark template" : "Mark as template"}
            </button>
          )}
          <button
            className="btn btn-line"
            onClick={() => viewFileInNewTab(invoicesApi.pdfUrl(inv.id).replace(/^\/clearbooks\/api/, ""))}
            title="Open the invoice PDF in a new tab"
          >
            PDF
          </button>
          {/* Record payment: only meaningful on a sent/issued invoice that
              isn't already fully paid or voided. Backend allocates the
              payment, updates paidCents + status, and links to the bank
              account for reconciliation. */}
          {!inv.is_template &&
            inv.status !== "void" &&
            inv.status !== "draft" &&
            BigInt(inv.paid_cents ?? "0") < BigInt(inv.total_cents ?? "0") && (
              <button className="btn btn-primary" onClick={() => setPaying(true)}>
                Record payment
              </button>
            )}
          {!inv.is_template && inv.public_token && (
            <PortalLinkButton token={inv.public_token} />
          )}
          {!inv.is_template && inv.status !== "void" && inv.status !== "paid" && (
            <button className="btn btn-ghost" onClick={voidInvoice}>Void</button>
          )}
        </div>
      </div>

      {paying && (
        <RecordPaymentModal
          direction="in"
          invoiceId={inv.id}
          remainingCents={String(BigInt(inv.total_cents ?? "0") - BigInt(inv.paid_cents ?? "0"))}
          onClose={() => setPaying(false)}
          onPaid={() => {
            setPaying(false);
            qc.invalidateQueries({ queryKey: ["invoice", inv.id] });
          }}
        />
      )}

      {inv.is_template && (
        <div className="banner" style={{
          margin: "12px 0", padding: "10px 14px",
          background: "var(--gold-soft)", color: "#6E5320",
          borderRadius: 8, fontSize: 13,
        }}>
          This is a <strong>template</strong>. It won't show in the main list and can't be sent. Click <strong>Clone</strong> to create a fresh draft you can send.
        </div>
      )}

      <div className="card card-pad-lg" style={{ padding: 0 }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={{ ...th, padding: "14px 18px" }}>Description</th>
              <th style={{ ...th, padding: "14px 18px", textAlign: "right" }}>Qty</th>
              <th style={{ ...th, padding: "14px 18px", textAlign: "right" }}>Unit</th>
              <th style={{ ...th, padding: "14px 18px", textAlign: "right" }}>Subtotal</th>
              <th style={{ ...th, padding: "14px 18px", textAlign: "right" }}>VAT</th>
              <th style={{ ...th, padding: "14px 18px", textAlign: "right" }}>WHT</th>
            </tr>
          </thead>
          <tbody>
            {inv.lines.map((l) => (
              <tr key={l.id} style={{ borderBottom: "0.5px solid var(--line)" }}>
                <td style={{ padding: "14px 18px", fontSize: 13 }}>{l.description}</td>
                <td style={{ padding: "14px 18px", fontSize: 13, textAlign: "right" }} className="num">
                  {l.quantity}
                </td>
                <td style={{ padding: "14px 18px", fontSize: 13, textAlign: "right" }} className="num">
                  {fmtCents(l.unit_price_cents, inv.currency)}
                </td>
                <td style={{ padding: "14px 18px", fontSize: 13, textAlign: "right", fontWeight: 500 }} className="num">
                  {fmtCents(l.line_subtotal_cents, inv.currency)}
                </td>
                <td style={{ padding: "14px 18px", fontSize: 13, textAlign: "right" }} className="num">
                  {fmtCents(l.vat_amount_cents, inv.currency)}
                </td>
                <td style={{ padding: "14px 18px", fontSize: 13, textAlign: "right" }} className="num">
                  {fmtCents(l.wht_amount_cents, inv.currency)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ padding: "20px 24px", display: "grid", gridTemplateColumns: "1fr 320px", gap: 24 }}>
          <div style={{ color: "var(--ink-3)", fontSize: 13 }}>{inv.notes ?? ""}</div>
          <div>
            <Row label="Subtotal" value={fmtCents(inv.subtotal_cents, inv.currency)} />
            <Row label="VAT" value={fmtCents(inv.vat_total_cents, inv.currency)} />
            <Row label="SSCL" value={fmtCents(inv.sscl_total_cents, inv.currency)} />
            <Row label="WHT (deducted)" value={`-${fmtCents(inv.wht_total_cents, inv.currency)}`} />
            <Row label="Total" value={fmtCents(inv.total_cents, inv.currency)} bold />
            <Row label="Paid" value={fmtCents(inv.paid_cents, inv.currency)} />
            {BigInt(inv.paid_cents ?? "0") > 0n && BigInt(inv.paid_cents ?? "0") < BigInt(inv.total_cents ?? "0") && (
              <Row
                label="Balance due"
                value={fmtCents(
                  String(BigInt(inv.total_cents ?? "0") - BigInt(inv.paid_cents ?? "0")),
                  inv.currency,
                )}
                bold
              />
            )}
          </div>
        </div>
      </div>

      <div style={{ marginTop: 20, display: "grid", gridTemplateColumns: "1fr 320px", gap: 20 }}>
        <PaymentsList kind="invoice" entityId={inv.id} currency={inv.currency} />
        <AttachmentList entityType="invoice" entityId={inv.id} readOnly={inv.status === "void"} />
      </div>

      <HistoryDrawer entityType="invoice" entityId={inv.id} />
    </>
  );
}

// ─── PaymentsList — payments allocated to one invoice or bill ────────
// Per-row: amount, date (small caption), bank account, "Receipt" (invoices
// only), "Reverse" button. Shamil 2026-06-24 — needed for the reverse flow
// + the "show date of payment in small font" ask + receipt issuing.
function PaymentsList({
  kind,
  entityId,
  currency,
}: {
  kind: "invoice" | "bill";
  entityId: string;
  currency: string;
}) {
  const qc = useQueryClient();
  const params = kind === "invoice" ? { invoiceId: entityId } : { billId: entityId };
  const pq = useQuery({
    queryKey: ["payments", kind, entityId],
    queryFn: () => paymentsApi.list(params),
  });
  const banksQ = useQuery({ queryKey: ["bank-accounts"], queryFn: () => bankApi.listAccounts() });
  const banksById = Object.fromEntries((banksQ.data?.accounts ?? []).map((b) => [b.id, b.accountName]));
  const reverseM = useMutation({
    mutationFn: (id: string) => paymentsApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["payments", kind, entityId] });
      qc.invalidateQueries({ queryKey: [kind === "invoice" ? "invoice" : "bill", entityId] });
      qc.invalidateQueries({ queryKey: ["history", kind, entityId] });
    },
  });

  const payments = pq.data?.payments ?? [];

  return (
    <div className="card" style={{ padding: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 10 }}>
        <h4 style={{ fontSize: 14, fontWeight: 600, color: "var(--ink-1)", margin: 0 }}>Payments</h4>
        {payments.length > 0 && (
          <span style={{ fontSize: 11, color: "var(--ink-4)" }}>{payments.length} recorded</span>
        )}
      </div>
      {pq.isLoading && <div style={{ fontSize: 12, color: "var(--ink-3)" }}>Loading…</div>}
      {!pq.isLoading && payments.length === 0 && (
        <div style={{ fontSize: 12, color: "var(--ink-3)", fontStyle: "italic" }}>No payments recorded yet.</div>
      )}
      {payments.map((p) => (
        <div key={p.id} style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          padding: "10px 0", borderTop: "0.5px solid var(--line)", gap: 12,
        }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 600, color: "var(--ink-1)" }} className="num">
              {fmtCents(p.amount_cents, currency)}
            </div>
            {/* Shamil 2026-06-24: payment date shown in small font under the amount. */}
            <div style={{ fontSize: 11, color: "var(--ink-4)", marginTop: 2 }}>
              {new Date(p.payment_date).toLocaleDateString()}
              {p.bank_account_id && banksById[p.bank_account_id] && (
                <> · {banksById[p.bank_account_id]}</>
              )}
              {p.reference && <> · {p.reference}</>}
            </div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            {kind === "invoice" && (
              <button
                type="button"
                className="btn btn-line"
                style={{ fontSize: 11, padding: "4px 8px" }}
                onClick={() => viewFileInNewTab(`/payments/${p.id}/receipt.pdf`)}
                title="Open the receipt PDF in a new tab"
              >
                Receipt
              </button>
            )}
            <button
              type="button"
              className="btn btn-ghost"
              style={{ fontSize: 11, padding: "4px 8px", color: "var(--rose, #b91c1c)" }}
              disabled={reverseM.isPending}
              onClick={() => {
                if (confirm(
                  `Reverse this ${fmtCents(p.amount_cents, currency)} payment? The ledger entry will be reversed and the ${kind} balance restored.`,
                )) reverseM.mutate(p.id);
              }}
            >
              {reverseM.isPending ? "…" : "Reverse"}
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Common ───────────────────────────────────────────────────────────
const billTabsStyle: React.CSSProperties = {
  display: "flex",
  gap: 4,
  padding: 4,
  background: "var(--paper-2)",
  borderRadius: 100,
  border: "0.5px solid var(--line)",
  marginBottom: 20,
  width: "fit-content",
};

const th: React.CSSProperties = {
  textAlign: "left",
  padding: "14px 18px",
  fontSize: 11,
  letterSpacing: "0.1em",
  textTransform: "uppercase",
  color: "var(--ink-4)",
  fontWeight: 500,
  background: "var(--paper)",
  borderBottom: "0.5px solid var(--line)",
};
const td: React.CSSProperties = {
  padding: "14px 18px",
  fontSize: 13,
  borderBottom: "0.5px solid var(--line)",
};

function StatusTag({ status }: { status: string }) {
  const colour = {
    draft: { bg: "var(--paper-2)", fg: "var(--ink-3)" },
    sent: { bg: "var(--gold-soft)", fg: "#6E5320" },
    partially_paid: { bg: "var(--accent-soft)", fg: "var(--accent-deep)" },
    paid: { bg: "var(--accent)", fg: "white" },
    overdue: { bg: "var(--rose-soft)", fg: "var(--rose)" },
    void: { bg: "var(--paper-3)", fg: "var(--ink-4)" },
    pending_approval: { bg: "var(--gold-soft)", fg: "#6E5320" },
    approved: { bg: "var(--accent-soft)", fg: "var(--accent-deep)" },
    rejected: { bg: "var(--rose-soft)", fg: "var(--rose)" },
  }[status] ?? { bg: "var(--paper-2)", fg: "var(--ink-3)" };
  return (
    <span
      style={{
        display: "inline-block",
        padding: "3px 9px",
        borderRadius: 100,
        fontSize: 11,
        fontWeight: 500,
        letterSpacing: "0.04em",
        textTransform: "uppercase",
        background: colour.bg,
        color: colour.fg,
      }}
    >
      {status.replace("_", " ")}
    </span>
  );
}

function fmtDate(s: string): string {
  return new Date(s).toLocaleDateString("en-LK", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export { StatusTag };
